import React, { useCallback, useEffect, useState } from 'react';
import NPOSubmits from './NPOSubmits';
import styled from 'styled-components';
import CompleteVerification from './CompleteVerification';
import IncompleteVerification from './IncompleteVerification';
import { AllVerificationsProgress, scrollToTopOnFeedback } from '../utils';
import { FeedbackStatus } from 'donor/portal/pages/FeedbackDashboard/api';
import { UserType } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { isAllVerified } from './utils';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import { submitReport } from '../../ReportBuilder/Components/Review/api';
import { useClient } from 'shared/apollo';
import FeedbackDoneScreen from './FeedbackDoneScreen';
import SubmitFeedback from './SubmitFeedback';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { useReport } from 'shared/providers/ReportProvider';
import { pullReportData, useTrackEvent } from 'shared/utils/useTrackEvent';
import { useUserActions } from 'shared/auth/hooks/useUserActions';

const StyledErrorText = styled.div`
  height: 100%;
  padding: ${SPACERS.medium};
  background-color: ${COLORS.whiteBase};
`;

const errorText = (status: FeedbackStatus, className?: string) => (
  <StyledErrorText className={className}>
    Unable to submit the report in <strong>{status}</strong> status.
  </StyledErrorText>
);

interface SubmitReadyProps {
  total: number;
  isReady: boolean;
  reportId: number;
  userType: UserType;
  feedbackStatus: FeedbackStatus;
  progress: AllVerificationsProgress;
  isThirdPartyReport: boolean;
  className?: string;
  handleBack: () => unknown;
  refreshFeedbackStatus: (status: FeedbackStatus) => unknown;
}

const SubmitReady: React.FC<SubmitReadyProps> = ({
  total,
  userType,
  reportId,
  progress,
  isThirdPartyReport,
  feedbackStatus,
  isReady,
  handleBack,
  refreshFeedbackStatus,
  className,
}) => {
  const client = useClient();
  const trackEvent = useTrackEvent();
  const { userType: userStaticType } = useUserActions();
  const { report, isReportEndDatePassed } = useReport();
  const [nextFeedbackStatus, setNextFeedbackStatus] = useState<
    FeedbackStatus
  >();
  const [showDoneScreen, setShowDoneScreen] = useState<boolean>(false);
  const [
    requestUpdateReportState,
    makeUpdateReportRequest,
  ] = useGraphQLRequest();

  useEffect(() => {
    scrollToTopOnFeedback();
  }, []);

  const onDone = useCallback(
    (status: FeedbackStatus) => {
      setNextFeedbackStatus(status);
      const reportData =
        report.type === 'REQUEST_SUCCESS' ? report.data : undefined;
      const request = submitReport(client!, reportId, status);
      request.then(() => {
        trackEvent('ReportStatusChanged', {
          ...(reportData
            ? pullReportData(reportData, isReportEndDatePassed, userStaticType)
            : {}),
          previousStatus: reportData?.status,
          previousStage: reportData?.stage,
          status,
          stage: reportData?.stage,
        });
      });
      makeUpdateReportRequest(request);
      // it goes to SubmitFeedback component when you change the status
      // which means it doesn't show the done screen.
      // request.then(() => {
      //   refreshFeedbackStatus(status);
      // });
    },
    [reportId, setNextFeedbackStatus, makeUpdateReportRequest],
  );

  useEffect(() => {
    if (requestUpdateReportState.type !== 'REQUEST_SUCCESS') return;
    setShowDoneScreen(true);
  }, [requestUpdateReportState.type, setShowDoneScreen]);

  if (report.type === 'REQUEST_INIT' || report.type === 'REQUEST_START')
    return (
      <div className={className}>
        <TrueImpactLoading />
      </div>
    );

  if (report.type === 'REQUEST_ERROR') {
    return errorText(feedbackStatus, className);
  }

  const feedbackType = report.data?.feedbackType || 'No Feedback';
  const saving = requestUpdateReportState.type === 'REQUEST_START';
  const errorMessage =
    requestUpdateReportState.type === 'REQUEST_ERROR'
      ? requestUpdateReportState.error.message
      : undefined;

  if (showDoneScreen) {
    return (
      <FeedbackDoneScreen
        total={total}
        userType={userType}
        reportId={reportId}
        feedbackStatus={feedbackStatus}
        feedbackType={feedbackType}
        isThirdPartyReport={isThirdPartyReport}
        nextFeedbackStatus={nextFeedbackStatus}
        requestUpdateReportState={requestUpdateReportState}
        className={className}
        handleBack={handleBack}
      />
    );
  }

  switch (feedbackStatus) {
    case 'Awaiting TI Feedback':
    case 'Awaiting Funder Feedback':
      return (
        <SubmitFeedback
          total={total}
          userType={userType}
          reportId={reportId}
          isLoading={saving}
          feedbackType={feedbackType}
          feedbackStatus={feedbackStatus}
          errorMessage={errorMessage}
          isThirdPartyReport={isThirdPartyReport}
          requestUpdateReportState={requestUpdateReportState}
          onDone={onDone}
          className={className}
          handleBack={handleBack}
        />
      );

    case 'TI Feedback Provided':
    case 'Funder Feedback Provided':
      return (
        <NPOSubmits
          total={total}
          userType={userType}
          reportId={reportId}
          isLoading={saving}
          feedbackType={feedbackType}
          errorMessage={errorMessage}
          feedbackStatus={feedbackStatus}
          isThirdPartyReport={isThirdPartyReport}
          requestUpdateReportState={requestUpdateReportState}
          onDone={onDone}
          className={className}
          handleBack={handleBack}
        />
      );

    case 'Awaiting TI Verification':
    case 'Awaiting Funder Verification':
      if (isAllVerified(progress, total)) {
        return (
          <CompleteVerification
            total={total}
            userType={userType}
            reportId={reportId}
            isLoading={saving}
            feedbackType={feedbackType}
            errorMessage={errorMessage}
            feedbackStatus={feedbackStatus}
            isThirdPartyReport={isThirdPartyReport}
            requestUpdateReportState={requestUpdateReportState}
            onDone={onDone}
            className={className}
            handleBack={handleBack}
          />
        );
      }
      return (
        <IncompleteVerification
          total={total}
          userType={userType}
          reportId={reportId}
          isLoading={saving}
          feedbackType={feedbackType}
          errorMessage={errorMessage}
          feedbackStatus={feedbackStatus}
          isThirdPartyReport={isThirdPartyReport}
          requestUpdateReportState={requestUpdateReportState}
          onDone={onDone}
          className={className}
          handleBack={handleBack}
        />
      );
    default:
      return errorText(feedbackStatus, className);
  }
};

export default SubmitReady;
